import { Link } from 'react-router-dom';

function Placeholder({ children }: { children: string }) {
  return (
    <span className="bg-yellow-100 text-yellow-800 px-1 rounded font-mono text-sm">
      [{children}]
    </span>
  );
}

export function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-cream-100">
      <header className="bg-white border-b border-cream-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="font-fraunces text-2xl font-light tracking-tight text-navy-mid">
            <em className="font-medium text-primary-500" style={{ fontStyle: 'italic' }}>Kin</em>Care
          </Link>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">
            Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-10">
          Last updated: <Placeholder>DATE</Placeholder>
        </p>

        <section className="space-y-4 text-gray-700 leading-relaxed">
          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            1. Agreement to Terms
          </h2>
          <p>
            By accessing or using the KinCare platform operated by{' '}
            <Placeholder>COMPANY NAME</Placeholder> (&quot;we&quot;, &quot;us&quot;, or
            &quot;our&quot;), you agree to be bound by these Terms of Service. If you do not agree
            to these terms, please do not use our platform.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            2. Description of Service
          </h2>
          <p>
            KinCare is a family care coordination platform that helps families organise care
            responsibilities, track medications, manage appointments, and coordinate tasks among
            family members. The platform is designed for adult users coordinating care for family
            members and dependents.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">3. User Accounts</h2>
          <p>
            To use KinCare, you must create an account. You are responsible for:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Providing accurate and complete registration information</li>
            <li>Maintaining the security of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorised access</li>
          </ul>
          <p>
            You must be at least <Placeholder>MINIMUM AGE, e.g. 18</Placeholder> years old to
            create an account.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">4. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Use the platform for any unlawful purpose</li>
            <li>Upload false, misleading, or harmful information</li>
            <li>
              Attempt to access other users&apos; accounts or data outside your authorised family
              groups
            </li>
            <li>
              Interfere with or disrupt the platform&apos;s infrastructure or security measures
            </li>
            <li>Use automated tools to scrape or extract data from the platform</li>
            <li>Impersonate another person or entity</li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            5. Family Data and Sharing
          </h2>
          <p>
            When you create or join a family group on KinCare, you acknowledge that:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Information you add to a family group (tasks, medications, appointments, notes) will
              be visible to other members of that group according to their assigned role
            </li>
            <li>
              Family group owners can invite and remove members, and manage role permissions
            </li>
            <li>
              You are responsible for ensuring you have appropriate consent to share information
              about dependents within the family group
            </li>
            <li>
              Data within a family group is isolated from other family groups
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            6. Intellectual Property
          </h2>
          <p>
            The Kin platform, including its design, features, and content (excluding user-provided
            data), is owned by <Placeholder>COMPANY NAME</Placeholder> and protected by applicable
            intellectual property laws. You retain ownership of all data you submit to the platform.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            7. Important Disclaimers
          </h2>
          <div className="bg-accent-50 border border-accent-200 rounded-lg p-4">
            <p className="font-medium text-gray-900 mb-2">KinCare is not a medical service.</p>
            <p>
              The platform is a coordination tool and does not provide medical advice, diagnosis,
              or treatment. Always consult qualified healthcare professionals for medical decisions.
              Do not rely on KinCare as a substitute for professional medical care.
            </p>
          </div>
          <div className="bg-accent-50 border border-accent-200 rounded-lg p-4 mt-4">
            <p className="font-medium text-gray-900 mb-2">KinCare is not an emergency service.</p>
            <p>
              In case of a medical emergency, contact your local emergency services immediately.
              While KinCare provides emergency profile features for informational purposes, it is not
              designed or intended to replace emergency communication systems.
            </p>
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            8. Limitation of Liability
          </h2>
          <p>
            To the maximum extent permitted by law, <Placeholder>COMPANY NAME</Placeholder> shall
            not be liable for any indirect, incidental, special, consequential, or punitive damages,
            or any loss of profits or revenues, whether incurred directly or indirectly, or any loss
            of data, use, goodwill, or other intangible losses resulting from:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Your use of or inability to use the platform</li>
            <li>Any unauthorised access to your data</li>
            <li>Any interruption or cessation of the service</li>
            <li>
              Any actions taken or not taken based on information available through the platform
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">9. Termination</h2>
          <p>
            We may suspend or terminate your access to the platform at any time for violation of
            these terms, with reasonable notice where possible. You may delete your account at any
            time through your account settings. Upon termination, your right to use the platform
            ceases immediately, and your data will be handled in accordance with our Privacy Policy.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">10. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of{' '}
            <Placeholder>JURISDICTION, e.g. England and Wales</Placeholder>, without regard to
            conflict of law provisions. Any disputes shall be subject to the exclusive jurisdiction
            of the courts of <Placeholder>JURISDICTION</Placeholder>.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">
            11. Changes to These Terms
          </h2>
          <p>
            We reserve the right to modify these Terms at any time. We will provide notice of
            material changes by posting the updated terms on this page and updating the &quot;Last
            updated&quot; date. Your continued use of the platform after changes constitutes
            acceptance of the modified terms.
          </p>

          <h2 className="text-xl font-semibold text-gray-900 mt-10 mb-4">12. Contact Us</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Email: <Placeholder>CONTACT EMAIL ADDRESS</Placeholder>
            </li>
            <li>
              Address: <Placeholder>COMPANY REGISTERED ADDRESS</Placeholder>
            </li>
          </ul>
        </section>
      </main>

      <footer className="border-t border-cream-200 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap gap-6 text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-gray-700">
            Privacy Policy
          </Link>
          <Link to="/cookies" className="hover:text-gray-700">
            Cookie Policy
          </Link>
          <Link to="/terms" className="hover:text-gray-700">
            Terms of Service
          </Link>
        </div>
      </footer>
    </div>
  );
}
